import { TaskAPI } from 'api/tasks';
import { mergeTaskFields, taskCollection } from './task-collection';
/**
 * Centralized READ wrappers over `TaskAPI`. Consumers should call these instead
 * of `TaskAPI.*` directly so every fetched `Task` flows through
 * `taskCollection.push(...)` — keeping the collection the single source of
 * truth — while the original API result is returned unchanged.
 *
 * Each wrapper forwards the exact arguments to its `TaskAPI` method (signatures
 * are derived from `TaskAPI` via `Parameters<>` so they stay in lock-step) and
 * preserves the original return type.
 */ /** Narrows an unknown-ish value to `Task[]` for the push-if-array cases. */ const isTaskArray = (value)=>Array.isArray(value);
/**
 * Per-task refetch generation. Rapid task edits (e.g. three quick status
 * changes) each emit a WS update that triggers `fetchTask`. Those refetches can
 * resolve out of order, and the server has no monotonic version field to dedupe
 * by, so a late-resolving STALE refetch would otherwise push an old snapshot
 * and flicker the UI. We stamp each `fetchTask` call with an incrementing
 * generation and only push the result if it is still the latest in-flight
 * refetch for that task — older results are dropped (their server snapshot is
 * by definition staler than the newest refetch).
 */ const taskFetchGen = new Map();
/** GET project task list → push the returned tasks. */ export const fetchTaskList = async (...args)=>{
    const result = await TaskAPI.getTaskList(...args);
    taskCollection.push(result);
    return result;
};
/**
 * GET a single task (wraps `getTask`) → push `[result]`, return the `Task`.
 *
 * Only the latest in-flight refetch for a given task pushes into the
 * collection; older, out-of-order responses are dropped to avoid flicker (see
 * `taskFetchGen`). The result is always returned to the caller regardless.
 */ export const fetchTask = async (...args)=>{
    var _taskFetchGen_get;
    const [, taskId] = args;
    const gen = ((_taskFetchGen_get = taskFetchGen.get(taskId)) !== null && _taskFetchGen_get !== void 0 ? _taskFetchGen_get : 0) + 1;
    taskFetchGen.set(taskId, gen);
    const result = await TaskAPI.getTask(...args);
    if (taskFetchGen.get(taskId) === gen) {
        taskCollection.push([
            result
        ]);
    }
    return result;
};
/**
 * Push a PARTIAL task response onto the existing collection record.
 *
 * The detail view splits a task across two partial endpoints — default fields
 * and additional fields — neither of which is the whole task. A plain (replace)
 * push of one would drop the fields the other carries, AND clobber an
 * optimistic edit (e.g. a just-added assignee) that lands between the two
 * fetches. Merge mode (`{ ...existing, ...incoming }`) layers the partial view
 * on top of absent keys — but `fields` needs deeper care: get-default-fields
 * returns ONLY the status field in `fields`, so a shallow merge would replace
 * the full custom-field array with that one-element array (emptying every
 * custom-field cell in the grid/gantt). Merge `fields` element-wise by
 * `fieldId` instead — same rule the collection's own `mergeChanges` applies to
 * optimistic field edits.
 */ const pushPartialTask = (result)=>{
    const existing = taskCollection.get(result.taskId);
    if (existing && result.fields) {
        taskCollection.push([
            {
                ...result,
                fields: mergeTaskFields(existing.fields, result.fields)
            }
        ], {
            mode: 'merge'
        });
        return;
    }
    taskCollection.push([
        result
    ], {
        mode: 'merge'
    });
};
/** GET a task's default fields → merge onto the existing record (see above). */ export const fetchTaskDefaultFields = async (...args)=>{
    var // The default-fields payload embeds this task's subtasks (taskId, name,
    // parentTask, project, status). Explode them into the collection so the
    // drawer's subtask table can read them via the `byParent` index WITHOUT a
    // separate full-project `fetchTaskList`. Merge mode + element-wise field
    // merge (via pushPartialTask) preserves any richer subtask snapshot already
    // present (e.g. dates from a prior fetch) and never clobbers a subtask's
    // custom-field cells with the status-only `fields` array.
    _result_subTasks;
    const result = await TaskAPI.getTaskDetailsDefaultFields(...args);
    pushPartialTask(result);
    ((_result_subTasks = result.subTasks) !== null && _result_subTasks !== void 0 ? _result_subTasks : []).forEach((subTask)=>{
        if ((subTask === null || subTask === void 0 ? void 0 : subTask.taskId) != null) {
            pushPartialTask(subTask);
        }
    });
    return result;
};
/** GET a task's additional fields → merge onto the existing record (see above). */ export const fetchTaskAdditionalFields = async (...args)=>{
    const result = await TaskAPI.getTaskDetailsAdditionalFields(...args);
    pushPartialTask(result);
    return result;
};
/** GET subtasks of a task → push the returned tasks. */ export const fetchSubTasks = async (...args)=>{
    const result = await TaskAPI.getSubTasks(...args);
    taskCollection.push(result);
    return result;
};
/** POST bulk-task-details by ids → push the returned tasks. */ export const fetchBulkTaskDetails = async (...args)=>{
    const result = await TaskAPI.getBulkTaskDetails(...args);
    taskCollection.push(result);
    return result;
};
/**
 * GET tasks & milestones for a project. `getTasksAndMilestonesForProjects`
 * resolves to `Task | Task[]` (the transformer's default generic), so we only
 * push when the result is an array.
 */ export const fetchTasksAndMilestones = async (...args)=>{
    const result = await TaskAPI.getTasksAndMilestonesForProjects(...args);
    if (isTaskArray(result)) {
        taskCollection.push(result);
    }
    return result;
};
/** GET paginated task list → push `data`. */ export const fetchPaginatedTaskList = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskList(...args);
    taskCollection.push(result.data);
    return result;
};
/** GET paginated task list scoped to a project → push `data`. */ export const fetchPaginatedTaskListWithProject = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskListWithProject(...args);
    taskCollection.push(result.data);
    return result;
};
/** GET paginated recently-assigned tasks → push `data`. */ export const fetchRecentlyAssignedTasks = async (...args)=>{
    const result = await TaskAPI.getPaginatedRecentlyAssignedTaskList(...args);
    taskCollection.push(result.data);
    return result;
};
/** POST paginated task list scoped to a project → push `data`. */ export const postPaginatedTasksWithProject = async (...args)=>{
    const result = await TaskAPI.postPaginatedTaskListWithProject(...args);
    taskCollection.push(result.data);
    return result;
};
/** POST filtered tasks → push `data`. */ export const postFilteredTasks = async (...args)=>{
    const result = await TaskAPI.postFilteredTasks(...args);
    taskCollection.push(result.data);
    return result;
};
/** GET all tasks scoped to a project → push `data`. */ export const fetchAllTasksWithProject = async (...args)=>{
    const result = await TaskAPI.getAllTaskListWithProject(...args);
    taskCollection.push(result.data);
    return result;
};
/** GET paginated tasks for a dashboard widget → push `data`. */ export const fetchDashboardTasks = async (...args)=>{
    const result = await TaskAPI.getPaginatedTaskListForDashbaord(...args);
    taskCollection.push(result.data);
    return result;
};
/**
 * POST paginated task list count. Count-only response (no `Task` entities) —
 * wrapped purely for centralization; nothing to push.
 */ export const fetchTaskCount = (...args)=>TaskAPI.postPaginatedTaskListCount(...args);
/** GET task search → push the returned tasks. */ export const searchTasks = async (...args)=>{
    const result = await TaskAPI.searchTasks(...args);
    taskCollection.push(result);
    return result;
};
/** GET timesheet task search → push the returned tasks. */ export const searchTasksForTimesheet = async (...args)=>{
    const result = await TaskAPI.searchTasksForTimesheet(...args);
    taskCollection.push(result);
    return result;
};
/**
 * GET subtask count for a project. No `Task` entities — wrapped purely for
 * centralization.
 */ export const fetchSubtaskCount = (...args)=>TaskAPI.getSubtaskCount(...args);
/**
 * POST unique field values for bulk editing. No `Task` entities — wrapped
 * purely for centralization.
 */ export const fetchTaskFieldValues = (...args)=>TaskAPI.getTaskFieldValues(...args);
/**
 * GET the account-level task field definitions. No `Task` entities — wrapped
 * purely for centralization.
 */ export const fetchTaskFields = (...args)=>TaskAPI.getTaskFields(...args);
/** Async CSV export for grouped task views. Nothing to push. */ export const exportTasksGrouped = (...args)=>TaskAPI.exportAsCSVAsyncForGroupType(...args);
/** Async CSV export for non-grouped task views. Nothing to push. */ export const exportTasksNonGrouped = (...args)=>TaskAPI.exportAsCSVAsyncForNonGroup(...args);
/** Async CSV export (V2) for grouped task views. Nothing to push. */ export const exportTasksGroupedV2 = (...args)=>TaskAPI.exportAsCSVAsyncForGroupTypeV2(...args);
/** Async CSV export (V2) for non-grouped task views. Nothing to push. */ export const exportTasksNonGroupedV2 = (...args)=>TaskAPI.exportAsCSVAsyncForNonGroupV2(...args);
/** Fire-and-forget filter logging endpoint. Nothing to push. */ export const logTaskFilters = (...args)=>TaskAPI.logFilters(...args);
/** Refetch full task detail + field slices into the collection. */ export const refetchTaskInCollection = async (projectId, taskId)=>{
    await Promise.all([
        fetchTask(projectId, taskId),
        fetchTaskDefaultFields({
            projectId,
            taskId
        }),
        fetchTaskAdditionalFields({
            projectId,
            taskId
        })
    ]);
};
